import React, { useMemo, useState } from 'react';
import { Search, X, BookOpen, FileText, ArrowLeft, ChevronRight, Calendar } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';

const DiarySection = ({
  isLoading,
  diary = [],
  theme,
  isDarkMode,
  handleNavigate
}) => {
  const pillStyle = { borderRadius: '9999px' };
  const cardStyle = { borderRadius: '32px' };
  const [searchQuery, setSearchQuery] = useState('');

  const formatReadableDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        const year = parts[0];
        const monthNum = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const malayMonths = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
          'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
        return `${day} ${malayMonths[monthNum]} ${year}`;
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
      }
    } catch (e) { /* ignore */ }
    return dateStr;
  };

  // Group entries by unique title+author
  const books = useMemo(() => {
    const map = new Map();
    (diary || []).forEach(entry => {
      const key = (entry.title || '').toLowerCase().trim() + '|||' + (entry.author || '').toLowerCase().trim();
      if (!map.has(key)) {
        map.set(key, {
          key,
          title: entry.title || 'Untitled',
          author: entry.author || '',
          type: entry.type || 'book',
          category: entry.category || '',
          entries: [],
          dates: [],
          topics: new Set()
        });
      }
      const book = map.get(key);
      book.entries.push(entry);
      if (entry.date) book.dates.push(entry.date);
      if (entry.topic?.trim()) book.topics.add(entry.topic.trim());
    });
    return Array.from(map.values()).map(book => {
      book.dates.sort();
      book.topicCount = book.topics.size;
      delete book.topics;
      return book;
    }).sort((a, b) => {
      const aDate = a.dates.length ? new Date(a.dates[a.dates.length - 1]) : new Date(0);
      const bDate = b.dates.length ? new Date(b.dates[b.dates.length - 1]) : new Date(0);
      return bDate - aDate;
    });
  }, [diary]);

  const filteredBooks = useMemo(() => {
    if (!searchQuery.trim()) return books;
    const q = searchQuery.toLowerCase();
    return books.filter(b =>
      (b.title || '').toLowerCase().includes(q) ||
      (b.author || '').toLowerCase().includes(q)
    );
  }, [books, searchQuery]);

  const handleCardClick = (book) => {
    handleNavigate('book-timeline', { bookKey: book.key, bookTitle: book.title, bookAuthor: book.author });
  };

  return (
    <section className="pt-32 pb-24 px-4 md:px-6 min-h-screen animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="max-w-4xl mx-auto w-full">

        <style>{`
          @keyframes staggerFadeUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .stagger-item { animation: staggerFadeUp 0.6s ease-out both; }
        `}</style>

        <button onClick={() => handleNavigate('home')}
          className={`group mb-8 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] opacity-50 hover:opacity-100 transition-opacity ${theme.text}`}>
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          <span>Back</span>
        </button>

        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <h2 className={`text-5xl md:text-6xl font-garamond mb-4 ${theme.text}`}>Bookshelf</h2>
            <p className={`text-sm tracking-wide opacity-60 leading-relaxed ${theme.textMuted}`}>
              Browse reflections grouped by book or article. {books.length} {books.length === 1 ? 'title' : 'titles'}.
            </p>
          </div>
          <div style={pillStyle} className={`relative flex items-center w-full md:w-64 px-6 py-3 border transition-all duration-500 ${
            isDarkMode ? 'bg-stone-900/40 border-stone-800 focus-within:border-stone-600' : 'bg-stone-50/50 border-stone-100 focus-within:border-stone-300'
          }`}>
            <Search size={14} className="opacity-40" />
            <input type="text" placeholder="Search books or authors..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ml-3 w-full bg-transparent text-xs font-medium outline-none placeholder:opacity-40" />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="ml-2 opacity-40 hover:opacity-100"><X size={14} /></button>
            )}
          </div>
        </div>

        {isLoading ? (
          <LoadingSpinner theme={theme} />
        ) : filteredBooks.length === 0 ? (
          <div className="text-center py-24 rounded-[32px] border border-dashed border-stone-200 dark:border-stone-800 opacity-60">
            <p className={`font-garamond italic text-xl ${theme.text}`}>No books found in this quiet space.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredBooks.map((book, index) => (
              <div key={book.key} style={{ animationDelay: `${index * 80}ms` }} className="stagger-item">
                <div onClick={() => handleCardClick(book)}
                  style={cardStyle}
                  className={`group p-6 md:p-8 border transition-all duration-700 cursor-pointer flex flex-col gap-4 ${
                    isDarkMode
                      ? 'border-transparent bg-stone-900/30 hover:border-stone-800/80 hover:bg-stone-900/60 hover:-translate-y-1'
                      : 'border-transparent bg-stone-50/50 hover:border-stone-100 hover:bg-stone-50/90 hover:shadow-lg hover:-translate-y-1'
                  }`}>
                  <div className="flex items-center flex-wrap gap-3 text-[10px] font-bold uppercase tracking-widest opacity-50">
                    <span className="flex items-center gap-1.5">
                      {book.type === 'book' ? <BookOpen size={12} /> : <FileText size={12} />}
                      {book.type === 'book' ? 'Book' : 'Article'}
                    </span>
                    {book.category && <><span className="opacity-30">•</span><span>{book.category}</span></>}
                    <span className="opacity-30">•</span>
                    <span>{book.entries.length} {book.entries.length === 1 ? 'reflection' : 'reflections'}</span>
                    {book.topicCount > 0 && (
                      <>
                        <span className="opacity-30">•</span>
                        <span>{book.topicCount} {book.topicCount === 1 ? 'topic' : 'topics'}</span>
                      </>
                    )}
                    {book.dates.length > 0 && (
                      <>
                        <span className="opacity-30">•</span>
                        <span className="flex items-center gap-1"><Calendar size={12} />{formatReadableDate(book.dates[book.dates.length - 1])}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-2xl font-garamond font-semibold leading-snug group-hover:translate-x-1 transition-transform ${theme.text}`}>{book.title}</h3>
                      {book.author && <p className={`text-xs font-semibold uppercase tracking-[0.1em] opacity-40 mt-1.5 ${theme.text}`}>By {book.author}</p>}
                    </div>
                    <ChevronRight size={16} className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-2 ${theme.text}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default DiarySection;
